import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, CircleCheck, Lock, Trophy, TriangleAlert } from 'lucide-react';
import {
  PERIOD_STATUS_LABEL,
  PeriodStatus,
  UserRole,
  type PeriodDto,
  type PeriodListDto,
} from '@weloze/shared';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDateTime } from '../../lib/format';
import { Alert, Badge, buttonVariants, Card, EmptyState, Skeleton } from '../../components/ui';

/**
 * `/admin/competencias` — a lista (PLAN §7, tela 12).
 *
 * A lista é montada pelo calendário, e não pelas linhas do banco: o mês ainda
 * aberto precisa aparecer, porque é justamente ele que a Qualidade procura ao
 * abrir a tela. Por isso uma competência sem nenhuma linha gravada aparece como
 * "Aberta" em vez de sumir.
 *
 * O aviso do topo é a razão de o fechamento ser manual: o portal lembra, a
 * pessoa decide. Fechar é o momento de conferir antes de o número virar oficial.
 */

const STATUS_TONE: Record<PeriodStatus, 'neutral' | 'warning' | 'success'> = {
  OPEN: 'neutral',
  CLOSED: 'warning',
  PUBLISHED: 'success',
};

export function PeriodsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.WELOZE_ADMIN;

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentHalf = now.getMonth() < 6 ? 1 : 2;

  const [data, setData] = useState<PeriodListDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.get<PeriodListDto>('/periods'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível carregar as competências.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Competências</h1>
          <p className="mt-1 text-sm text-slate-600">
            Fechar congela os índices do mês. Publicar é o momento em que o fornecedor passa a
            enxergá-los.
          </p>
        </div>
        {/* O semestre precisa de ano e metade na URL; por isso é alcançado
            daqui, e não por um item de menu que teria de adivinhar quais. */}
        <Link
          to={`/admin/competencias/semestre/${currentYear}/${currentHalf}`}
          className={buttonVariants({ variant: 'outline', size: 'sm' })}
        >
          <Trophy className="size-4" aria-hidden />
          Média do {currentHalf}º semestre
        </Link>
      </header>

      {error && <Alert>{error}</Alert>}

      {data && data.pendingClose.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/40">
          <div className="flex items-center gap-2">
            <CalendarClock className="size-4 text-amber-600" aria-hidden />
            <h2 className="font-medium text-slate-900">
              {data.pendingClose.length === 1
                ? '1 competência terminada e ainda aberta'
                : `${data.pendingClose.length} competências terminadas e ainda abertas`}
            </h2>
          </div>
          <p className="mt-1 text-sm text-slate-600">
            O mês acabou no calendário, mas os números ainda não foram congelados. Pré-visualize
            antes de fechar — depois de publicada, alterar exige justificativa e cria uma nova
            versão.
          </p>
        </Card>
      )}

      {!data ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      ) : data.periods.length === 0 ? (
        <EmptyState
          title="Nenhuma competência ainda"
          description="A lista começa no mês em que o primeiro fornecedor passou a ser monitorado."
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[44rem] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3 font-medium">Competência</th>
                <th className="px-4 py-3 font-medium">Situação</th>
                <th className="px-4 py-3 font-medium">Avaliações</th>
                <th className="px-4 py-3 font-medium">Quando</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.periods.map((period) => (
                <PeriodRow key={period.competence} period={period} isAdmin={isAdmin} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PeriodRow({ period, isAdmin }: { period: PeriodDto; isAdmin: boolean }) {
  const detail = `/admin/competencias/${period.year}/${period.month}`;

  return (
    <tr className="transition-colors hover:bg-slate-50/70">
      <td className="px-4 py-3">
        <Link to={detail} className="font-medium text-brand-700 underline-offset-4 hover:underline">
          {period.label}
        </Link>
        {period.hasAlterations && (
          <p className="mt-0.5 flex items-center gap-1 text-xs text-red-600">
            <TriangleAlert className="size-3" aria-hidden />
            alterada após a publicação
          </p>
        )}
      </td>

      <td className="px-4 py-3">
        <Badge tone={STATUS_TONE[period.status]}>
          {period.status === PeriodStatus.PUBLISHED && (
            <CircleCheck className="size-3" aria-hidden />
          )}
          {period.status === PeriodStatus.CLOSED && <Lock className="size-3" aria-hidden />}
          {PERIOD_STATUS_LABEL[period.status]}
        </Badge>
        {period.status === PeriodStatus.OPEN && period.isComplete && (
          <span className="ml-2 text-xs text-amber-700">mês terminado</span>
        )}
      </td>

      <td className="px-4 py-3 text-slate-600">
        {period.status === PeriodStatus.OPEN ? (
          <span className="text-slate-400">—</span>
        ) : (
          <>
            {period.evaluationCount}
            {period.unmeasuredCount > 0 && (
              <span className="ml-1 text-xs text-slate-500">
                ({period.unmeasuredCount} com índice sem apuração)
              </span>
            )}
          </>
        )}
      </td>

      <td className="px-4 py-3 text-xs text-slate-500">
        {period.publishedAt ? (
          <>publicada em {formatDateTime(period.publishedAt)}</>
        ) : period.closedAt ? (
          <>fechada em {formatDateTime(period.closedAt)}</>
        ) : (
          <span className="text-slate-400">—</span>
        )}
        {period.closedByName && <div>por {period.closedByName}</div>}
      </td>

      <td className="px-4 py-3 text-right">
        <Link to={detail} className={buttonVariants({ variant: 'outline', size: 'sm' })}>
          {period.status === PeriodStatus.OPEN && isAdmin ? 'Pré-visualizar' : 'Abrir'}
        </Link>
      </td>
    </tr>
  );
}
